import React, { useCallback, useEffect, useState } from 'react';
import { useLatest } from 'react-use';

import downloadFile from '@lyearn/core/utils/network/downloadFile';
import { useTranslation } from '@lyearn/i18n';
import { AlertTriangle, PaperPlane2 } from '@lyearn/icons';
import { Box, LinkButton, useSnackbar } from '@lyearn/ui';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import { JobEntityStatusEnum, JobPurposeEnum, JobSubscriptionOperationEnum } from '@/types/schema';

import { JobStateFragment } from '../../graphql/fragments/generated/JobState';
import { useJobQuery } from '../../graphql/queries/generated/job';
import { useJobByCreatorSubscription } from '../../graphql/subscription/generated/jobByCreator';
import useCancelJob from '../useCancelJob';

const SUCCESSFUL_SNACKBAR_DURATION = 6000;

const useImportJob = ({
  purpose,
  onImportStatusUpdate,
  refetchJobs,
  onShowUpdateDialogDetails,
  failedSnackbarMessage,
  importJobScheduleId,
  onComplete,
  preventRefetch = false,
  hideShowDetailsButton = false,
}: {
  purpose: JobPurposeEnum[];
  onImportStatusUpdate: (status: JobEntityStatusEnum) => void;
  refetchJobs: (...args: any[]) => void;
  onShowUpdateDialogDetails: () => void;
  failedSnackbarMessage: string;
  importJobScheduleId?: string;
  onComplete?: (job: JobStateFragment) => void;
  preventRefetch?: boolean;
  hideShowDetailsButton?: boolean;
}) => {
  const { id } = useUserContext();

  const [job, setJob] = useState<JobStateFragment | undefined>();

  const onImportStatusUpdateRef = useLatest(onImportStatusUpdate);

  const [{ data }] = useJobByCreatorSubscription({
    variables: {
      input: {
        createdById: id,
        purpose,
        operations: [
          JobSubscriptionOperationEnum.JobUpdated,
          JobSubscriptionOperationEnum.JobCreated,
        ],
      },
    },
  });
  const [{ data: jobData }] = useJobQuery({
    variables: { filters: { jobScheduleId: importJobScheduleId } },
    pause: !importJobScheduleId,
  });
  const { onCancelJob } = useCancelJob();

  useEffect(() => {
    if (data && data.jobByCreator.job.jobScheduleId === importJobScheduleId) {
      setJob(data.jobByCreator.job);
    }
    if (importJobScheduleId && !job && jobData?.job) {
      setJob(jobData.job);
    }
  }, [data, importJobScheduleId, job, jobData]);

  const { showSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const onDownloadReport = useCallback(async () => {
    const outputMediaUrl = job?.outputMediaUrls[0];
    if (outputMediaUrl?.url) {
      downloadFile(outputMediaUrl.url);
    }
  }, [job?.outputMediaUrls]);

  const onCancel = useCallback(() => {
    onCancelJob(job!.jobScheduleId);
  }, [job, onCancelJob]);

  useEffect(() => {
    if (!job || job.jobScheduleId !== importJobScheduleId) {
      return;
    }
    onImportStatusUpdateRef.current(job.status);
    if (!preventRefetch) {
      refetchJobs();
    }
    if (job.status === JobEntityStatusEnum.Completed) {
      showSnackbar({
        message: t('Common:BulkImport.ImportSuccessful'),
        startIcon: <PaperPlane2 className="h-20 w-20" fillColorToken="icon-white" />,
        duration: SUCCESSFUL_SNACKBAR_DURATION,
        action: hideShowDetailsButton ? null : (
          <LinkButton
            className="flex-shrink-0 py-2 px-4"
            color={'inverse'}
            onClick={onShowUpdateDialogDetails}>
            {t('Common:BulkImport.ShowDetails')}
          </LinkButton>
        ),
      });
      onComplete?.(job);
    }

    if (job.status === JobEntityStatusEnum.Cancelled) {
      showSnackbar({
        message: t('Common:BulkImport.ImportCanceled'),
        startIcon: <AlertTriangle className="h-20 w-20" fillColorToken="icon-error-light" />,
      });
    }

    if (job.status === JobEntityStatusEnum.Failed) {
      showSnackbar({
        message: failedSnackbarMessage,
        startIcon: <AlertTriangle className="h-20 w-20" fillColorToken="icon-error-light" />,
        duration: SUCCESSFUL_SNACKBAR_DURATION,
        action: (
          <Box className="flex-shrink-0">
            <LinkButton className="py-2 px-4" color={'inverse'} onClick={onDownloadReport}>
              {t('Common:BulkImport.DownloadReport')}
            </LinkButton>
          </Box>
        ),
      });
    }
  }, [
    onDownloadReport,
    failedSnackbarMessage,
    importJobScheduleId,
    job,
    onImportStatusUpdateRef,
    refetchJobs,
    showSnackbar,
    t,
    onComplete,
    onShowUpdateDialogDetails,
  ]);

  return { job, onDownloadReport, onCancel };
};

export default useImportJob;
